import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pharmacyPurchasingService,
  CreatePharmacyPurchaseOrderDto,
  CreatePharmacyGoodsReceiptDto,
  CreatePharmacyPurchaseReturnDto,
  GeneratePharmacyInvoiceFromGrnDto,
  PharmacyPurchaseOrderStatus,
} from '../pharmacyPurchasingService';

// Suppliers
export function usePharmacySuppliers(search?: string) {
  return useQuery({
    queryKey: ['pharmacy-suppliers', search],
    queryFn: () => pharmacyPurchasingService.getSuppliers(search),
    staleTime: 30000,
  });
}

export function useCreatePharmacySupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyPurchasingService.createSupplier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] }),
  });
}

export function useUpdatePharmacySupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      pharmacyPurchasingService.updateSupplier(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] }),
  });
}

export function useDeletePharmacySupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyPurchasingService.deleteSupplier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] }),
  });
}

export function usePaginatedPharmacySuppliers(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-suppliers', 'paginated', page, limit, search],
    queryFn: () => pharmacyPurchasingService.getSuppliersPaginated(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

/** Bulk trash runs silently so the page can show a single summary toast. */
export function useBulkDeletePharmacySuppliers() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => pharmacyPurchasingService.bulkDeleteSuppliers(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-supplier-trash'] });
    },
  });
}

export function usePharmacySupplierTrash(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-supplier-trash', page, limit, search],
    queryFn: () => pharmacyPurchasingService.getSupplierTrash(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

export function useRestorePharmacySupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.restoreSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-supplier-trash'] });
    },
  });
}

export function usePermanentlyDeletePharmacySupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.permanentDeleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-supplier-trash'] });
    },
  });
}

// Purchase orders
export function usePharmacyPurchaseOrders(filters?: { status?: string; supplierId?: string }) {
  return useQuery({
    queryKey: ['pharmacy-purchase-orders', filters],
    queryFn: () => pharmacyPurchasingService.getPurchaseOrders(filters),
    staleTime: 20000,
  });
}

export function usePharmacyPurchaseOrder(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-purchase-order', id],
    queryFn: () => pharmacyPurchasingService.getPurchaseOrder(id!),
    enabled: !!id,
  });
}

export function useCreatePharmacyPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePharmacyPurchaseOrderDto) =>
      pharmacyPurchasingService.createPurchaseOrder(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] }),
  });
}

export function useUpdatePharmacyPurchaseOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PharmacyPurchaseOrderStatus }) =>
      pharmacyPurchasingService.updatePurchaseOrderStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-order'] });
    },
  });
}

export function useCancelPharmacyPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyPurchasingService.cancelPurchaseOrder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] }),
  });
}

export function usePaginatedPharmacyPurchaseOrders(
  page = 1,
  limit = 20,
  filters?: { status?: string; supplierId?: string; search?: string },
) {
  return useQuery({
    queryKey: ['pharmacy-purchase-orders', 'paginated', page, limit, filters],
    queryFn: () => pharmacyPurchasingService.getPurchaseOrdersPaginated(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

export function useBulkDeletePharmacyPurchaseOrders() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => pharmacyPurchasingService.bulkDeletePurchaseOrders(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-order-trash'] });
    },
  });
}

export function usePharmacyPurchaseOrderTrash(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-purchase-order-trash', page, limit, search],
    queryFn: () => pharmacyPurchasingService.getPurchaseOrderTrash(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

export function useRestorePharmacyPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.restorePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-order-trash'] });
    },
  });
}

export function usePermanentlyDeletePharmacyPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.permanentDeletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-order-trash'] });
    },
  });
}

// Goods receipts
export function usePharmacyGoodsReceipts(filters?: { supplierId?: string; poId?: string }) {
  return useQuery({
    queryKey: ['pharmacy-goods-receipts', filters],
    queryFn: () => pharmacyPurchasingService.getGoodsReceipts(filters),
    staleTime: 20000,
  });
}

export function usePharmacyGoodsReceipt(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-goods-receipt', id],
    queryFn: () => pharmacyPurchasingService.getGoodsReceipt(id!),
    enabled: !!id,
  });
}

export function useCreatePharmacyGoodsReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePharmacyGoodsReceiptDto) =>
      pharmacyPurchasingService.createGoodsReceipt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-order'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] });
    },
  });
}

export function useGeneratePharmacyInvoiceFromGrn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      grnId,
      data,
    }: {
      grnId: string;
      data?: GeneratePharmacyInvoiceFromGrnDto;
    }) => pharmacyPurchasingService.generateInvoiceFromGoodsReceipt(grnId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipt'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] });
    },
  });
}

export function usePaginatedPharmacyGoodsReceipts(
  page = 1,
  limit = 20,
  filters?: { supplierId?: string; poId?: string; search?: string },
) {
  return useQuery({
    queryKey: ['pharmacy-goods-receipts', 'paginated', page, limit, filters],
    queryFn: () => pharmacyPurchasingService.getGoodsReceiptsPaginated(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

export function useBulkDeletePharmacyGoodsReceipts() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => pharmacyPurchasingService.bulkDeleteGoodsReceipts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipt-trash'] });
    },
  });
}

export function usePharmacyGoodsReceiptTrash(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-goods-receipt-trash', page, limit, search],
    queryFn: () => pharmacyPurchasingService.getGoodsReceiptTrash(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 20000,
  });
}

export function useRestorePharmacyGoodsReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.restoreGoodsReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipt-trash'] });
    },
  });
}

export function usePermanentlyDeletePharmacyGoodsReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyPurchasingService.permanentDeleteGoodsReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipt-trash'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
    },
  });
}

// Payables
export function usePharmacyPayables(filters?: {
  status?: string;
  supplierId?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ['pharmacy-payables', filters],
    queryFn: () => pharmacyPurchasingService.getPayables(filters),
    staleTime: 20000,
  });
}

export function useCreatePharmacySupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyPurchasingService.createInvoice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] }),
  });
}

export function usePharmacySupplierInvoice(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-supplier-invoice', id],
    queryFn: () => pharmacyPurchasingService.getInvoice(id!),
    enabled: !!id,
  });
}

export function useDeletePharmacySupplierInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => pharmacyPurchasingService.bulkDeleteInvoices(ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] }),
  });
}

export function useCreatePharmacySupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyPurchasingService.createPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-supplier-payments'] });
    },
  });
}

// Purchase returns (debit notes to suppliers)
export function usePharmacyPurchaseReturns(filters?: { grnId?: string; supplierId?: string }) {
  return useQuery({
    queryKey: ['pharmacy-purchase-returns', filters],
    queryFn: () => pharmacyPurchasingService.getPurchaseReturns(filters),
    staleTime: 20000,
  });
}

export function useCreatePharmacyPurchaseReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ grnId, data }: { grnId: string; data: CreatePharmacyPurchaseReturnDto }) =>
      pharmacyPurchasingService.createPurchaseReturn(grnId, data),
    meta: { successMessage: 'Purchase return recorded' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-purchase-returns'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-goods-receipt'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-payables'] });
    },
  });
}
